/**
 * GuardStatusBadge — S10-08
 *
 * Zeigt Guard-Status als Ampel-Badge (ok/warning/critical) mit Tooltip.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export type GuardStatus = 'ok' | 'warning' | 'critical'

type Props = {
  guard_status: GuardStatus | null | undefined
  failed_guards?: string[] | null
}

function getColor(status: GuardStatus | null | undefined): string {
  if (!status) return 'var(--text-subtle)'
  switch (status) {
    case 'ok':
      return 'var(--success)'
    case 'warning':
      return 'var(--warning)'
    case 'critical':
      return 'var(--danger)'
  }
}

function getBgColor(status: GuardStatus | null | undefined): string {
  if (!status) return 'var(--bg-inset)'
  switch (status) {
    case 'ok':
      return 'rgba(16, 185, 129, 0.15)'
    case 'warning':
      return 'rgba(245, 158, 11, 0.15)'
    case 'critical':
      return 'rgba(239, 68, 68, 0.15)'
  }
}

export function GuardStatusBadge({ guard_status, failed_guards }: Props) {
  const { t } = useTranslation('hotspots')
  const [showTooltip, setShowTooltip] = useState(false)

  const STATUS_LABELS: Record<GuardStatus, string> = {
    ok: t('guard.labels.ok'),
    warning: t('guard.labels.warning'),
    critical: t('guard.labels.critical'),
  }

  const SHORT_LABELS: Record<GuardStatus, string> = {
    ok: t('guard.shortLabels.ok'),
    warning: t('guard.shortLabels.warn'),
    critical: t('guard.shortLabels.crit'),
  }

  const GUARD_NAMES: Record<string, string> = {
    online: t('guard.guardNames.online'),
    heartbeat: t('guard.guardNames.heartbeat'),
    sync: t('guard.guardNames.sync'),
    reward: t('guard.guardNames.reward'),
    ip: t('guard.guardNames.ip'),
  }

  if (!guard_status) {
    return (
      <span
        className="guard-badge guard-unknown"
        title={t('guard.unknownTitle')}
        aria-label={t('guard.unknownLabel')}
      >
        <span className="guard-dot" style={{ backgroundColor: 'var(--text-subtle)' }} />
      </span>
    )
  }

  const color = getColor(guard_status)
  const bgColor = getBgColor(guard_status)
  const label = STATUS_LABELS[guard_status]

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="guard-badge"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        aria-label={t('guard.statusLabel', { label })}
        style={{
          backgroundColor: bgColor,
          border: `1.5px solid ${color}`,
          borderRadius: '6px',
          padding: '4px 8px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <span
          className="guard-dot"
          style={{
            backgroundColor: color,
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            display: 'inline-block',
          }}
        />
        <span style={{ color, fontSize: '12px', fontWeight: 500 }}>
          {SHORT_LABELS[guard_status]}
        </span>
      </button>

      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            marginTop: '4px',
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            padding: '8px 12px',
            zIndex: 100,
            minWidth: '180px',
            boxShadow: 'var(--shadow-md)',
          }}
          role="tooltip"
          data-testid="guard-status-tooltip"
        >
          {failed_guards && failed_guards.length > 0 ? (
            <>
              <div style={{ fontSize: '11px', color: 'var(--text-subtle)', marginBottom: '4px' }}>
                {t('guard.failedTitle')}
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {failed_guards.map((guard) => (
                  <li
                    key={guard}
                    style={{
                      fontSize: '12px',
                      color: 'var(--danger)',
                      padding: '2px 0',
                    }}
                  >
                    • {GUARD_NAMES[guard] ?? guard}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            // heliumtracker-web#24: "Guard-Alarm-Status: CRIT" stand vorher ohne
            // jede Erklärung da, wenn das BE keine failed_guards-Liste mitliefert.
            // Fallback-Erklärung statt eines nackten Kürzels.
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {t(`guard.statusExplanations.${guard_status}`)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}